import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Save, Eye, X, Plus, Trash2, AlertTriangle, Image as ImageIcon, Video } from 'lucide-react';
import { toast } from 'sonner';
import { updateLibraryResource } from '../../lib/api';
import { LIBRARY_CATEGORIES } from '../../data/library';
import MediaEmbed from '../MediaEmbed';
import { detectMediaType, isCanvaShareLink, resolveLibraryMedia } from '../../lib/mediaEmbed';

const TYPES = [
  { id: 'video', zh: '视频', en: 'Video' },
  { id: 'image_guide', zh: '图文', en: 'Image Guide' },
  { id: 'guide', zh: '指南', en: 'Guide' },
];

const MAX_IMAGES = 3;

function Field({ label, hint, children }) {
  return (
    <label className="block mb-4">
      <span className="block text-xs uppercase tracking-wider text-ink-tertiary mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-ink-tertiary mt-1 leading-relaxed">{hint}</span>}
    </label>
  );
}

// Inline Canva warning shown below any URL input that points at a Canva share
// page (which is not a direct image URL and will not render in <img>).
function CanvaWarning({ url, lang }) {
  if (!isCanvaShareLink(url)) return null;
  return (
    <div
      className="mt-2 p-2.5 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2"
      data-testid="canva-warning"
    >
      <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
      <span className="text-[11px] text-amber-900 leading-relaxed">
        {lang === 'zh'
          ? 'Canva 分享链接不是直接图片网址。请从 Canva 下载 JPG/PNG 后上传，再使用图片网址。'
          : 'A Canva share link is not a direct image URL. Please export the design as JPG/PNG, upload it, and use the image URL.'}
      </span>
    </div>
  );
}

// Preview block for a single image URL. Falls back to a hint when the URL is
// not yet a valid direct image link.
function ImagePreview({ url, alt = '' }) {
  if (!url) return null;
  const t = detectMediaType(url);
  if (t !== 'image') return null;
  return (
    <div className="mt-2 rounded-xl overflow-hidden border border-border bg-subtle/40">
      <img
        src={url}
        alt={alt}
        className="w-full max-h-48 object-cover"
        onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
      />
    </div>
  );
}

export default function LibraryEditDrawer({ resource, open, onClose, token, onSaved }) {
  const { lang } = useLanguage();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (resource) {
      // Resolve legacy media_url into the new fields on load so admins always
      // see the same data shape when editing.
      const { videoUrl, imageUrls } = resolveLibraryMedia(resource);
      setForm({
        title_zh: resource.title_zh || '',
        title_en: resource.title_en || '',
        type: resource.type || 'guide',
        category: resource.category || 'basics',
        duration: resource.duration || '',
        desc_zh: resource.desc_zh || '',
        desc_en: resource.desc_en || '',
        thumbnail_url: resource.thumbnail_url || '',
        video_url: resource.video_url || videoUrl || '',
        image_urls: (resource.image_urls && resource.image_urls.length ? resource.image_urls : imageUrls).slice(0, MAX_IMAGES),
      });
    }
  }, [resource]);

  if (!form || !resource) return null;

  const update = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const updateImageAt = (idx, value) => {
    setForm((p) => {
      const next = [...(p.image_urls || [])];
      next[idx] = value;
      return { ...p, image_urls: next };
    });
  };

  const addImage = () => {
    setForm((p) => {
      const next = [...(p.image_urls || [])];
      if (next.length >= MAX_IMAGES) return p;
      next.push('');
      return { ...p, image_urls: next };
    });
  };

  const removeImageAt = (idx) => {
    setForm((p) => {
      const next = [...(p.image_urls || [])];
      next.splice(idx, 1);
      return { ...p, image_urls: next };
    });
  };

  const save = async () => {
    // Block save if any field contains a Canva share link — they are not
    // direct image URLs and would render as broken in the public gallery.
    const offending = [form.thumbnail_url, ...(form.image_urls || [])].find(isCanvaShareLink);
    if (offending) {
      toast.error(
        lang === 'zh'
          ? 'Canva 分享链接无法当作图片使用，请先导出为 JPG/PNG。'
          : 'Canva share links cannot be used as images — export as JPG/PNG first.',
      );
      return;
    }

    setSaving(true);
    try {
      // Clean blank entries before sending.
      const payload = {
        ...form,
        image_urls: (form.image_urls || []).map((s) => (s || '').trim()).filter(Boolean).slice(0, MAX_IMAGES),
        video_url: (form.video_url || '').trim(),
      };
      // For full backwards-compat: mirror video_url back into legacy media_url
      // so any older viewer code still sees something useful.
      payload.media_url = payload.video_url || (payload.image_urls[0] || '') || resource.media_url || '';
      await updateLibraryResource(resource.id, payload, token);
      toast.success(lang === 'zh' ? '已保存' : 'Saved');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(lang === 'zh' ? '保存失败' : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const videoMediaType = detectMediaType(form.video_url);
  const imageUrls = form.image_urls || [];

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto bg-paper"
        data-testid="library-edit-drawer"
      >
        <SheetHeader className="mb-6">
          <SheetTitle className="font-heading text-xl text-ink text-left">
            {lang === 'zh' ? '编辑资料' : 'Edit Resource'}
            <span className="block text-xs text-ink-tertiary font-normal mt-1 font-sans">
              {resource.id}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label={lang === 'zh' ? '标题（中文）' : 'Title (ZH)'}>
            <input data-testid="lib-edit-title-zh" className="input-base" value={form.title_zh} onChange={(e) => update('title_zh', e.target.value)} />
          </Field>
          <Field label={lang === 'zh' ? '标题（英文）' : 'Title (EN)'}>
            <input data-testid="lib-edit-title-en" className="input-base" value={form.title_en} onChange={(e) => update('title_en', e.target.value)} />
          </Field>

          <Field label={lang === 'zh' ? '类型' : 'Type'}>
            <select data-testid="lib-edit-type" className="input-base" value={form.type} onChange={(e) => update('type', e.target.value)}>
              {TYPES.map((tp) => (
                <option key={tp.id} value={tp.id}>{lang === 'zh' ? tp.zh : tp.en}</option>
              ))}
            </select>
          </Field>
          <Field label={lang === 'zh' ? '分类' : 'Category'}>
            <select data-testid="lib-edit-category" className="input-base" value={form.category} onChange={(e) => update('category', e.target.value)}>
              {LIBRARY_CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{lang === 'zh' ? c.zh : c.en}</option>
              ))}
            </select>
          </Field>

          <Field label={lang === 'zh' ? '时长（选填）' : 'Duration (optional)'}>
            <input data-testid="lib-edit-duration" placeholder={lang === 'zh' ? '例如：1 min' : 'e.g. 1 min'} className="input-base" value={form.duration || ''} onChange={(e) => update('duration', e.target.value)} />
          </Field>
          <Field
            label={lang === 'zh' ? '缩略图 URL（选填）' : 'Thumbnail URL (optional)'}
            hint={lang === 'zh'
              ? '只控制 Library 卡片封面图；留空时会自动使用 YouTube 缩略图或第一张图片。'
              : 'Controls the Library card cover only. Leave empty to auto-use the YouTube thumbnail or the first supporting image.'}
          >
            <input data-testid="lib-edit-thumbnail" placeholder="https://... or /uploads/cover.jpg" className="input-base" value={form.thumbnail_url} onChange={(e) => update('thumbnail_url', e.target.value)} />
            <CanvaWarning url={form.thumbnail_url} lang={lang} />
            <ImagePreview url={form.thumbnail_url} alt="thumbnail" />
          </Field>
        </div>

        {/* Teaching video */}
        <div className="rounded-2xl border border-border bg-subtle/30 p-4 mb-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink mb-3">
            <Video size={14} className="text-terracotta" />
            {lang === 'zh' ? '教学视频 URL（选填）' : 'Teaching Video URL (optional)'}
          </h3>
          <input
            data-testid="lib-edit-video-url"
            placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
            className="input-base"
            value={form.video_url}
            onChange={(e) => update('video_url', e.target.value)}
          />
          <span className="block text-[11px] text-ink-tertiary mt-1 leading-relaxed">
            {lang === 'zh'
              ? '支持 YouTube、Vimeo、mp4、webm、ogg、mov。'
              : 'Supports YouTube, Vimeo, mp4, webm, ogg, mov.'}
          </span>
          {form.video_url && (
            <div className="mt-3" data-testid="lib-edit-video-preview">
              <div className="flex items-center gap-2 mb-2">
                <Eye size={12} className="text-terracotta" />
                <span className="text-[10px] uppercase tracking-wider text-ink-tertiary">
                  {lang === 'zh' ? '视频预览' : 'Video preview'}
                </span>
                {videoMediaType && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-paper text-ink-secondary">
                    {videoMediaType}
                  </span>
                )}
              </div>
              <MediaEmbed url={form.video_url} title={form.title_en} />
            </div>
          )}
        </div>

        {/* Supporting images */}
        <div className="rounded-2xl border border-border bg-subtle/30 p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
              <ImageIcon size={14} className="text-terracotta" />
              {lang === 'zh' ? '辅助图片（最多 3 张）' : `Supporting Images (up to ${MAX_IMAGES})`}
            </h3>
            <button
              type="button"
              onClick={addImage}
              disabled={imageUrls.length >= MAX_IMAGES}
              data-testid="lib-edit-image-add"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-paper border border-terracotta/30 text-xs font-medium text-terracotta hover:bg-terracotta hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={12} />
              {lang === 'zh' ? '新增' : 'Add'}
            </button>
          </div>
          <p className="text-[11px] text-ink-tertiary mb-3 leading-relaxed">
            {lang === 'zh'
              ? '支持直接图片网址（jpg / png / webp 等）和上传后的相对路径，例如 /uploads/latch.jpg。Canva 分享链接不可直接使用。'
              : 'Direct image URLs (jpg / png / webp …) and uploaded paths like /uploads/latch.jpg are supported. Canva share links cannot be used directly.'}
          </p>
          {imageUrls.length === 0 && (
            <p className="text-[11px] text-ink-tertiary italic" data-testid="lib-edit-image-empty">
              {lang === 'zh' ? '尚未添加任何辅助图片。' : 'No supporting images yet.'}
            </p>
          )}
          {imageUrls.map((u, i) => (
            <div key={i} className="mb-3" data-testid={`lib-edit-image-row-${i}`}>
              <div className="flex items-start gap-2">
                <input
                  data-testid={`lib-edit-image-${i}`}
                  placeholder={i === 0 ? 'https://... or /uploads/photo.jpg' : `https://...`}
                  className="input-base flex-1"
                  value={u}
                  onChange={(e) => updateImageAt(i, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeImageAt(i)}
                  data-testid={`lib-edit-image-remove-${i}`}
                  className="p-2 rounded-xl border border-border text-ink-tertiary hover:text-risk-red hover:border-risk-red flex-shrink-0"
                  aria-label="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <CanvaWarning url={u} lang={lang} />
              <ImagePreview url={u} alt={`image ${i + 1}`} />
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-x-4">
          <Field label={lang === 'zh' ? '简介（中文）' : 'Description (ZH)'}>
            <textarea data-testid="lib-edit-desc-zh" rows={4} className="input-base" value={form.desc_zh} onChange={(e) => update('desc_zh', e.target.value)} />
          </Field>
          <Field label={lang === 'zh' ? '简介（英文）' : 'Description (EN)'}>
            <textarea data-testid="lib-edit-desc-en" rows={4} className="input-base" value={form.desc_en} onChange={(e) => update('desc_en', e.target.value)} />
          </Field>
        </div>

        <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-paper border-t border-border/60 flex gap-2 justify-end">
          <button type="button" onClick={onClose} data-testid="lib-edit-cancel" className="btn-outline">
            <X size={14} className="mr-1" />
            {lang === 'zh' ? '取消' : 'Cancel'}
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid="lib-edit-save" className="btn-primary disabled:opacity-60">
            <Save size={14} className="mr-1" />
            {saving ? (lang === 'zh' ? '保存中…' : 'Saving…') : (lang === 'zh' ? '保存' : 'Save')}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
